import PropTypes from "prop-types";
import { useState, useEffect, useCallback, useMemo } from "react";

// @mui
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { LoadingButton } from "@mui/lab";
// locales
import { useLocales } from "src/locales";
//
import Iconify from "src/components/iconify/iconify";
import { useSnackbar } from "notistack";
import { useBoolean } from "src/hooks/use-boolean";
// api
import { get, save, remove } from "src/api/localStorage";
// components
import { Button as DXButton } from "devextreme-react";

export default function DxLocalStorage({ load = false, code, dataGrid }) {
  const { t } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const [localStorage, setLocalStorage] = useState(null);
  const [name, setName] = useState("");

  const show = useBoolean(false);
  const open = useBoolean(false);

  const [options, setOptions] = useState([]);
  const loading = useMemo(() => open.value && options.length === 0, [open.value, options]);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if (load && code && show)
          get(code).then(data => {
            if (data.length > 0) {
              setOptions([...data]);
            } else {
              open.onFalse();
            }
          });
      }
    })();

    return () => {
      active = false;
    };
  }, [loading, code, load, open, show]);

  useEffect(() => {
    if (!open.value) {
      setOptions([]);
    }
  }, [open.value]);

  const handleShow = useCallback(() => {
    show.onTrue();
    setName("");
    setLocalStorage(null);
  }, [show]);

  const handleChange = useCallback(e => {
    setName(e.target.value);
  }, []);

  const handleSelectorChange = useCallback((e, data) => {
    setLocalStorage(data);
  }, []);

  const onRemove = useCallback(
    event => {
      remove(localStorage.id)
        .then(data => {
          enqueueSnackbar(t("successRemoveFilter"), { variant: "success" });

          get(code).then(data => {
            setLocalStorage(null);
            setOptions(data);
          });
        })
        .catch(error => error);
    },
    [enqueueSnackbar, code, t, localStorage?.id]
  );

  const handleSave = useCallback(async () => {
    if (load) {
      if (!localStorage?.data) return;

      dataGrid.current.instance.state(JSON.parse(localStorage?.data));

      show.onFalse();
    } else {
      if (!name?.trim()) {
        enqueueSnackbar(t("nameRequired"), { variant: "error" });
        return;
      }

      dataGrid.current.instance.clearSelection();
      const data = {
        name,
        key: code,
        data: JSON.stringify(dataGrid.current.instance.state()),
      };

      setIsSaving(true);

      try {
        await save(data);

        enqueueSnackbar(t("successSaveFilter"), { variant: "success" });

        show.onFalse();
      } catch {}

      setIsSaving(false);
    }
  }, [dataGrid, enqueueSnackbar, code, load, localStorage?.data, show, t, name]);

  return (
    <div>
      {load ? (
        <DXButton
          icon="folder"
          hint={t("loadFilters")}
          onClick={handleShow}
        />
      ) : (
        <DXButton
          icon="floppy"
          hint={t("saveFilters")}
          onClick={handleShow}
        />
      )}

      <Dialog
        fullWidth
        maxWidth="sm"
        open={show.value}
        onClose={show.onFalse}
      >
        <DialogTitle>{load ? t("loadFilters") : t("saveFilters")}</DialogTitle>

        <DialogContent
          dividers
          sx={{ pt: 1 }}
        >
          <Stack spacing={3}>
            {load ? (
              <Autocomplete
                fullWidth
                open={open.value}
                onOpen={open.onTrue}
                onClose={open.onFalse}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                getOptionLabel={option => option.name}
                options={options}
                loading={loading}
                value={localStorage}
                onChange={handleSelectorChange}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t("filter")}
                    value={localStorage}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : localStorage ? (
                            <Tooltip title={t("remove")}>
                              <IconButton
                                color="error"
                                onClick={onRemove}
                              >
                                <Iconify icon="ic:outline-delete" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                name="name"
                label={t("name")}
                fullWidth
                value={name || ""}
                onChange={handleChange}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            type="button"
            variant="soft"
            loading={isSaving}
            onClick={handleSave}
            color="primary"
          >
            {load ? t("loadFilter") : t("saveFilter")}
          </LoadingButton>
          <Button
            onClick={show.onFalse}
            color="error"
            variant="soft"
          >
            {t("cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

DxLocalStorage.propTypes = {
  load: PropTypes.bool,
  code: PropTypes.string,
  dataGrid: PropTypes.object,
};
